/**
 * AddAssignmentTaskFields.tsx
 * Task-type-specific fields for the Add Assignment dialog.
 *
 * Phase 3, Item 5 — extracted to keep AddAssignmentDialog.tsx under 600 lines.
 * Follow-up 1.7 — task autocomplete wired to real search endpoint.
 * Wave 3 (percent retirement): the "Allocation (%)" input is GONE — a task
 * assignment is presence (busy on its covered days; server pins Percent=100).
 *
 * Renders:
 *   - ScheduleTask autocomplete (searches by name, debounced 300 ms)
 *   - Week (Saturday start) date picker with snap-to-Saturday
 */

import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { TaskSearchResult } from '../../../services/scheduleTasks';
import {
  formatTaskAutocompleteLabel,
  taskSourceChipLabel,
} from '../../../utils/taskAutocompleteLabel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AddAssignmentTaskFieldsProps {
  /** Currently selected task (null = none selected) */
  selectedTask: TaskSearchResult | null;
  onTaskChange: (task: TaskSearchResult | null) => void;

  /** Search query driving the autocomplete */
  taskSearchQuery: string;
  onTaskSearchQueryChange: (q: string) => void;

  /** Options returned from backend search */
  taskOptions: TaskSearchResult[];
  loadingTasks: boolean;

  /** ISO 'YYYY-MM-DD' Saturday — week the task is allocated to */
  weekStart: string;
  onWeekStartChange: (ws: string) => void;
}

// ---------------------------------------------------------------------------
// Helper: snap date to Saturday of the selected week
// ---------------------------------------------------------------------------

function snapToSaturday(dateStr: string): string {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun,6=Sat
  const diff = dow === 6 ? 0 : -(dow + 1);
  const sat = new Date(Date.UTC(y, m - 1, d + diff));
  return sat.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AddAssignmentTaskFields: React.FC<AddAssignmentTaskFieldsProps> = ({
  selectedTask,
  onTaskChange,
  taskSearchQuery,
  onTaskSearchQueryChange,
  taskOptions,
  loadingTasks,
  weekStart,
  onWeekStartChange,
}) => {
  const theme = useTheme();

  return (
    <>
      {/* ── Schedule Task autocomplete ─────────────────────────────── */}
      <Box>
        <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
          Schedule Task (optional — leave blank for stand-alone allocation)
        </Typography>
        <Autocomplete<TaskSearchResult>
          options={taskOptions}
          loading={loadingTasks}
          getOptionLabel={(t) => formatTaskAutocompleteLabel(t)}
          isOptionEqualToValue={(a, b) => a.ScheduleTaskKey === b.ScheduleTaskKey}
          filterOptions={(opts) => opts} // server-side search — no client-side filtering
          inputValue={taskSearchQuery}
          onInputChange={(_, v) => onTaskSearchQueryChange(v)}
          value={selectedTask}
          onChange={(_, v) => onTaskChange(v)}
          size="small"
          noOptionsText={
            taskSearchQuery.length < 2
              ? 'Type 2+ characters to search tasks…'
              : 'No matching tasks'
          }
          renderOption={(props, option) => (
            <li {...props} key={option.ScheduleTaskKey}>
              <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                    {option.Name}
                  </Typography>
                  {/* Source chip: Quote number or WO id */}
                  <Chip
                    label={taskSourceChipLabel(option)}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: theme.schedule?.bookingTentative
                        ? `${theme.schedule.bookingTentative}33`
                        : theme.table.headerBg,
                      color: theme.palette.text.secondary,
                      border: `1px solid ${theme.border.color}`,
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                  {/* Task type chip */}
                  {option.TaskTypeName && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {option.TaskTypeName}
                    </Typography>
                  )}
                </Box>
                {/* Date range */}
                {(option.StartDate || option.EndDate) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.68rem', mt: 0.1 }}
                  >
                    {option.StartDate ?? '?'} → {option.EndDate ?? '?'}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search tasks by name…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingTasks && <CircularProgress size={14} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.68rem', mt: 0.5, display: 'block' }}
        >
          Linking to a task is optional. If selected, dates will be pre-filled from the task.
        </Typography>
      </Box>

      {/* ── Week start ────────────────────────────────────────────── */}
      <Box>
        <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
          Week (Saturday start)
        </Typography>
        <TextField
          type="date"
          size="small"
          value={weekStart}
          onChange={(e) => onWeekStartChange(snapToSaturday(e.target.value))}
          helperText="Will snap to the Saturday of the selected week."
          sx={{ maxWidth: 220 }}
        />
      </Box>
    </>
  );
};
